import type { MyPermissions, PermissionScope } from '../../hooks/usePermission';
import type { Catalog, Grant, Role } from '../../hooks/useRolesApi';

export const SCOPE_ORDER: PermissionScope[] = ['self', 'reports', 'subtree', 'company'];
export const rank = (s?: PermissionScope) => (s ? SCOPE_ORDER.indexOf(s) : -1);

/** Права роли списком: «Все права» раскрываются по каталогу минус `except`, как на сервере. */
export const grantsOf = (role: Pick<Role, 'grants' | 'except'>, catalog: Catalog): Grant[] =>
  role.grants === '*'
    ? catalog.permissions
        .filter((p) => !role.except.includes(p.key))
        .map((p) => ({ permission: p.key, scope: 'company' as const }))
    : role.grants;

/**
 * Могу ли я распоряжаться правом — как `holds` на сервере: оно у меня с тем же или более
 * широким охватом, а «своё» (`self`) другим — только при праве на всю компанию.
 */
export const canHold = (mine: Partial<Record<string, PermissionScope>>, g: Grant) =>
  g.scope === 'self' ? mine[g.permission] === 'company' : rank(mine[g.permission]) >= rank(g.scope);

/**
 * Права роли, которые я не могу выдать (`POST /roles/assignments`) или забрать. Себе (`isSelf`)
 * — ещё и ничего, что откроет своё, если своё у меня не открыто (`own`). Пусто — можно.
 */
export const beyondMine = (
  role: Pick<Role, 'grants' | 'except' | 'isAll'>,
  catalog: Catalog,
  perms: MyPermissions | undefined,
  isSelf = false,
): string[] => {
  if (perms?.isOwner) return [];
  const own = new Set(perms?.own ?? []);
  return grantsOf(role, catalog)
    .filter(
      (g) =>
        !canHold(perms?.grants ?? {}, g) ||
        (isSelf && (g.scope === 'self' || role.isAll) && !own.has(g.permission)),
    )
    .map((g) => g.permission);
};
